// ─────────────────────────────────────────────────────────────────────────────
// FTUE anchors
// ─────────────────────────────────────────────────────────────────────────────
// The closed list of semantic anchor keys a tip may point at. A tip whose
// anchor is not declared here fails the registry test, so a renamed or deleted
// anchor cannot silently produce a tip that never shows.
//
// Anchors are attached in templates with the `appFtueAnchor` directive:
//
//   <div [appFtueAnchor]="'order-company'"> … </div>
//
// They are NOT `data-testid` values: test hooks and product anchors change for
// different reasons, so the two vocabularies are kept apart even where they
// land on the same element.
// ─────────────────────────────────────────────────────────────────────────────

export const FTUE_ANCHORS = {
  /** The company tag / "missing company" chip on an order detail. */
  ORDER_COMPANY: 'order-company',

  /** The "always use this product for code X" toggle bar of the line matcher. */
  ORDER_LINE_ALWAYS_MATCH: 'order-line-always-match',

  /**
   * The match-status icon of the FIRST order line matched by alternative rather
   * than by exact code — the one whose tooltip reads "Review alternatives".
   * Only the first such line carries the anchor: several lines on one order can
   * be in this state, and a ring on each of them is noise, not guidance.
   */
  ORDER_LINE_ALTERNATIVES: 'order-line-alternatives',

  /** The unmatched-product icon of the FIRST unmatched line, same rule. */
  ORDER_LINE_UNMATCHED: 'order-line-unmatched',

  /** The sliders icon button that opens the orders table's column picker. */
  ORDERS_COLUMN_SETTINGS: 'orders-column-settings',

  /**
   * The list/group switch above an order's product table. Declared for the order
   * DETAIL page only: the side-by-side panel shows the same control, but a user
   * who opens it has already chosen to review the order line by line, and the
   * announcement would land on top of that work rather than introduce it.
   */
  ORDER_LINE_GROUPING: 'order-line-grouping',
} as const;

export type FtueAnchorKey = (typeof FTUE_ANCHORS)[keyof typeof FTUE_ANCHORS];

/** Every declared key, for the registry test and the dev inspector. */
export const FTUE_ANCHOR_KEYS: readonly FtueAnchorKey[] = Object.values(FTUE_ANCHORS);
